import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Modal, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket from 'react-use-websocket';

import CopyIcon from '../../../../../assets/svg/copyIcon.svg?react';
import { GCM } from '../../../../../assets/utils/cipher';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import { setIsMeetingSent } from '../../../../../slices/instantMeetingSlice';
import { RootState } from '../../../../../utils/store';

interface InstantMeetingModalProps {
  open: boolean;
  setClose: () => void;
  conversation: string;
}

interface WSSendMessage {
  message: string;
  to_user?: number;
  action: string;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 480,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '12px',
  p: 4,
};

const headerStyle = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '18px',
};

const bodyTextStyle = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 400,
  fontSize: '16px',
  wordBreak: 'break-all',
};

const buttonStyle = {
  textTransform: 'none',
  bgcolor: '#2449FC',
  borderRadius: '50px',
  color: '#FFFFFF',
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '16px',
  '&:hover': {
    bgcolor: '#4E7AFF',
    color: '#FFFFFF',
  },
  marginTop: '12px',
};

const InstantMeetingModal = ({
  open,
  setClose,
  conversation,
}: InstantMeetingModalProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [sending, setSending] = useState<boolean>(false);
  const meeting = useSelector((state: RootState) => state.instantMeeting.item);
  const loading = useSelector(
    (state: RootState) => state.instantMeeting.loading,
  );
  const error = useSelector((state: RootState) => state.instantMeeting.error);
  const isMeetingSent = useSelector(
    (state: RootState) => state.instantMeeting.isMeetingSent,
  );
  const region = useSelector((state: RootState) => state.auth.user.region);

  const WS_PATH = import.meta.env.VITE_WS_PATH;
  const LINK = '/report-chat';
  const CONVERSATION_PATH = `/${conversation}`;

  const SOCKET_URL = `${import.meta.env.VITE_WSS_PROTOCOL
    }${WS_PATH}${LINK}${CONVERSATION_PATH}/`;

  const gcm = useMemo(() => new GCM(), []);

  const accessToken = localStorage.getItem('accessToken');

  const { sendMessage, readyState } = useWebSocket(SOCKET_URL, {
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: () => true, // closeEvent
    queryParams: {
      token: `${accessToken}`,
      region: region ? region : 'eu',
    },
  });

  const CopyToClipBoard = () => {
    navigator.clipboard.writeText(meeting.link).then(() => {
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'Copied to clipboard' }),
        'success',
      );
    });
  };

  const sendMeetingMessageHandler = (meetingLink: string) => {
    const messageText = `The case manager hosted a video meeting. To join the video meeting: ${meetingLink}`;
    if (conversation) {
      // Description in-transit encryption preparation
      const cipherText = gcm.encrypt({ plainText: messageText });
      const chat: WSSendMessage = {
        message: cipherText,
        action: 'create',
      };
      sendMessage(JSON.stringify(chat));
      dispatch(setIsMeetingSent(true));
    }
  };

  const startCallHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (meeting.link) {
      setSending(true);
      if (!isMeetingSent)
        // CHECK IF MEETING IS ALREADY SENT OR NOT
        sendMeetingMessageHandler(meeting.link);
      const urlArray = meeting.link.split('/');
      const meetingId = urlArray[urlArray.length - 1];
      window.open(`/meetings/${meetingId}`, '_blank'); // Open in a new tab
      event.preventDefault();
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={setClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Stack sx={style} gap={3}>
        <Stack direction='row' justifyContent='space-between'>
          <Typography sx={headerStyle}>Your meeting is ready</Typography>
          <IconButton sx={{ padding: '0px' }} onClick={setClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography sx={bodyTextStyle}>
          <FormattedMessage defaultMessage='Please copy and share this meeting link to the Confide messenger to facilitate your meeting.' />
        </Typography>
        <Stack
          direction='row'
          justifyContent='space-between'
          sx={{
            bgcolor: '#F6F6F6',
            borderRadius: '6px',
            padding: '12px 16px',
          }}
        >
          <Typography sx={bodyTextStyle}>{meeting.link}</Typography>
          <IconButton
            disabled={error || loading}
            sx={{ padding: '0px' }}
            onClick={CopyToClipBoard}
          >
            <CopyIcon />
          </IconButton>
        </Stack>
        <Button
          component='a'
          href=''
          sx={buttonStyle}
          disabled={error || loading || readyState != 1}
          onClick={startCallHandler}
        >
          {sending
            ? intl.formatMessage({
              defaultMessage: 'Sending Meeting Link to Reporter...',
            })
            : intl.formatMessage({ defaultMessage: 'Start Call' })}
        </Button>
      </Stack>
    </Modal>
  );
};

export default InstantMeetingModal;
